import React, { useEffect, useState } from 'react';
import {
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  getRedirectResult,
} from 'firebase/auth';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { auth } from '../firebase';
import { useAuth } from './AuthContext';
import { setCachedEmail, getCachedEmail, clearCachedEmail } from './emailCache';

const EMAIL_KEY = 'utilibill:ownerEmail';

type Mode =
  | 'choose'
  | 'magic-form'
  | 'magic-sent'
  | 'magic-confirm-email'
  | 'completing'
  | 'error';

export default function SignIn() {
  const { signInWithGoogle, completeEmailLinkSignIn } = useAuth();
  const [mode, setMode] = useState<Mode>('choose');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    const stored = getCachedEmail(EMAIL_KEY);
    if (stored) {
      void doCompleteMagic(stored);
    } else {
      setMode('magic-confirm-email');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Complete any social sign-in that fell back to a full-page redirect (e.g.
  // when the popup was blocked). onAuthStateChanged handles the resulting
  // session; we only need to surface failures here.
  useEffect(() => {
    getRedirectResult(auth).catch((err: any) => {
      setError(socialErrorMessage(err));
      setMode('error');
    });
  }, []);

  const doCompleteMagic = async (addr: string) => {
    setMode('completing');
    try {
      await completeEmailLinkSignIn(addr);
      clearCachedEmail(EMAIL_KEY);
    } catch (err: any) {
      setError(err?.message ?? 'Link expired or already used. Request a new one.');
      setMode('error');
    }
  };

  const handleSendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await sendSignInLinkToEmail(auth, email, {
        url: `${window.location.origin}/sign-in`,
        handleCodeInApp: true,
      });
      setCachedEmail(EMAIL_KEY, email);
      setMode('magic-sent');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to send link');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err: any) {
      setError(err?.message ?? 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'completing') {
    return (
      <Box sx={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', bgcolor: 'background.default',
      }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={32} thickness={3} />
          <Typography variant="body2" color="text.secondary">Signing you in…</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      bgcolor: 'background.default',
      p: 2,
    }}>
      <Box sx={{
        width: '100%',
        maxWidth: 400,
        bgcolor: 'background.paper',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        p: { xs: 3, sm: 4 },
        boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
      }}>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 400, mb: 0.5, color: 'text.primary' }}
          >
            Utilibill
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Property water billing, simplified.
          </Typography>
        </Box>

        {/* Choose mode */}
        {mode === 'choose' && (
          <Stack spacing={2}>
            <Button
              variant="outlined"
              fullWidth
              size="large"
              startIcon={<GoogleIcon />}
              disabled={loading}
              onClick={handleGoogle}
              sx={{ justifyContent: 'flex-start', px: 2.5 }}
            >
              Continue with Google
            </Button>

            <Divider sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>or</Divider>

            <Button
              variant="outlined"
              fullWidth
              size="large"
              startIcon={<EmailOutlinedIcon />}
              disabled={loading}
              onClick={() => { setError(null); setMode('magic-form'); }}
              sx={{ justifyContent: 'flex-start', px: 2.5 }}
            >
              Continue with email link
            </Button>

            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
          </Stack>
        )}

        {/* Email link form */}
        {mode === 'magic-form' && (
          <Stack component="form" spacing={2} onSubmit={handleSendLink}>
            <Typography variant="body2" color="text.secondary">
              We'll send a one-click sign-in link to your email.
            </Typography>
            <TextField
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              size="medium"
              autoFocus
            />
            <Button type="submit" variant="contained" disabled={loading} fullWidth size="large">
              {loading ? 'Sending…' : 'Send sign-in link'}
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => { setError(null); setMode('choose'); }}
              sx={{ color: 'text.secondary' }}
            >
              ← Back
            </Button>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}

        {/* Email sent */}
        {mode === 'magic-sent' && (
          <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center' }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: '50%',
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(37,99,235,0.18)' : '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <EmailOutlinedIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" gutterBottom>Check your inbox</Typography>
              <Typography variant="body2" color="text.secondary">
                We sent a sign-in link to <strong>{email}</strong>. The link expires in 1 hour.
              </Typography>
            </Box>
            <Button
              variant="text" size="small"
              onClick={() => setMode('magic-form')}
              sx={{ color: 'text.secondary' }}
            >
              Resend link
            </Button>
          </Stack>
        )}

        {/* Confirm email (link opened on different device) */}
        {mode === 'magic-confirm-email' && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Enter the email address you used to request this sign-in link.
            </Typography>
            <TextField
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              size="medium"
              autoFocus
            />
            <Button
              variant="contained"
              disabled={!email}
              fullWidth
              size="large"
              onClick={() => void doCompleteMagic(email)}
            >
              Continue
            </Button>
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}

        {/* Error recovery */}
        {mode === 'error' && (
          <Stack spacing={2}>
            <Alert severity="error">{error}</Alert>
            <Button variant="outlined" onClick={() => { setError(null); setMode('choose'); }}>
              Try again
            </Button>
          </Stack>
        )}
      </Box>
    </Box>
  );
}

/**
 * Map Firebase auth errors from social sign-in into user-facing copy. A
 * provider that isn't enabled server-side returns `auth/operation-not-allowed`,
 * which we explain rather than leak verbatim.
 */
function socialErrorMessage(err: any, label?: string): string {
  const code = err?.code as string | undefined;
  const who = label ? `${label} sign-in` : 'Sign-in';
  if (code === 'auth/operation-not-allowed') {
    return `${who} isn't available yet. Please try another option.`;
  }
  if (code === 'auth/account-exists-with-different-credential') {
    return 'An account already exists with this email. Sign in with your original method.';
  }
  return err?.message ?? `${who} failed`;
}
